import { userKeeper } from '@mail-core/dashboard';
import { Rubles } from 'Cloud/Application/Rubles';
import classNames from 'clsx';
import { parse, stringify } from 'qs';
import React, { type ReactElement, memo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    IS_B2B_BIZ_USER,
    IS_HIDDEN_PUBLIC,
    IS_MY_TEAM,
    IS_PUBLIC_FOLDER,
    IS_PUBLIC_OF_OVERQUOTA,
    NEW_PORTAL_HEADER,
} from 'reactApp/appHelpers/configHelpers';
import { isAttachesNoLineAd } from 'reactApp/appHelpers/featuresHelpers/features/attachSidebarAd';
import { overquotaUserLockInPublic, publicOverquota } from 'reactApp/appHelpers/featuresHelpers/features/publicOverquota';
import { getFeatureABDisableAdsSnackbar } from 'reactApp/modules/features/features.selectors';
import { isOwnPublic, isWeblinkDownloadable } from 'reactApp/modules/public/public.selectors';
import { loadPublicAlbumPageRequest } from 'reactApp/modules/public/publicAlbum.actions';
import { selectStatusPage } from 'reactApp/modules/router/router.selectors';
import type { EStorageType } from 'reactApp/modules/storage/storage.types';
import { addToUploadQueue } from 'reactApp/modules/uploading/uploading.module';
import { isUserOverquotaFromApi } from 'reactApp/modules/userQuota/overquota.helpers';
import { getOverQuota } from 'reactApp/modules/userQuota/userQuota.selectors';
import { ErrorPage } from 'reactApp/sections/ErrorPage/ErrorPage';
import { useRouteChangeProcessing } from 'reactApp/sections/MainPage/hooks/useRouteChangeProcessing';
import { File } from 'reactApp/ui/Public/Components/File/File';
import { Folder } from 'reactApp/ui/Public/Components/Folder/Folder';
import { initDisableAdAnalytics } from 'reactApp/ui/Public/Public.helpers';
import { ShovePublic } from 'reactApp/ui/Public/ShovePublic/ShovePublic';
import ReactViewerConnected from 'reactApp/ui/ReactViewer/ReactViewer';
import { filterFiles, startAdsUpdateTime } from 'reactApp/utils/helpers';

import { BreadcrumbsWrapper } from './Components/BreadcrumbsWrapper/BreadcrumbsWrapper';
import { Overquota } from './Components/Overquota/Overquota';
import { Password } from './Components/Password/Password';
import { ThemeChooser } from './Components/ThemeChooser/ThemeChooser';
import styles from './Public.css';

interface Props {
    storage: EStorageType;
    noAds: boolean;
    isFolder: boolean;
    isShove: boolean;
    isAlbum: boolean;
    supportThemedPage?: boolean;
    showThemeChooser?: boolean;
}

// eslint-disable-next-line max-lines-per-function, complexity
export const Public = memo(function Public({
    storage,
    noAds,
    isFolder,
    isShove,
    isAlbum,
    supportThemedPage,
    showThemeChooser,
}: Props): ReactElement {
    const showDisableButton = useSelector(getFeatureABDisableAdsSnackbar) !== 'b';
    const dispatch = useDispatch();
    const statusPage = useSelector(selectStatusPage);
    const isDownloadable = useSelector(isWeblinkDownloadable);
    const isOwn = useSelector(isOwnPublic);
    const isNotDownloadableFolder = isFolder && !isDownloadable;
    const isBizDownlodableFolder = IS_B2B_BIZ_USER && IS_PUBLIC_FOLDER && isDownloadable;
    const showDisabledForOverquotaOwner = IS_PUBLIC_OF_OVERQUOTA && publicOverquota;
    const isOverquota = useSelector(isUserOverquotaFromApi);
    const quota = useSelector(getOverQuota);
    const noTopBanner = isAttachesNoLineAd || noAds;

    useRouteChangeProcessing({
        storage,
        path: location.pathname.replace(`/${isAlbum ? 'album' : storage}/`, ''),
        search: location.search,
    });

    useEffect(() => {
        userKeeper.timeEnd('cloud-public-ready');

        initDisableAdAnalytics(storage, showDisableButton);

        dispatch(loadPublicAlbumPageRequest());

        if (isShove || noAds) {
            return;
        }

        const stopAdsIpdate = startAdsUpdateTime(() => Rubles.updateRightCol());

        return () => {
            stopAdsIpdate?.();
        };
    }, [isShove, noAds, showDisableButton]);

    useEffect(() => {
        const uploadFilesFromClipboard = async (e: ClipboardEvent) => {
            if (!e.clipboardData?.files.length) {
                return;
            }

            const files = filterFiles(Array.from(e.clipboardData.files));

            if (!files.length) {
                return;
            }

            e.preventDefault();

            dispatch(
                addToUploadQueue({
                    files,
                })
            );
        };

        document.addEventListener('paste', uploadFilesFromClipboard);
        return () => document.removeEventListener('paste', uploadFilesFromClipboard);
    }, []);

    useEffect(() => {
        // Удаляем query-параметр pin из урла после загрузки
        if (typeof window.history.replaceState === 'function') {
            const search = parse(window.location.search, { ignoreQueryPrefix: true }) || {};

            if (search?.pin) {
                delete search.pin;
                const query = stringify(search, { addQueryPrefix: true });
                window.history.replaceState(null, '', `${window.location.pathname}${query}`);
            }
        }
    }, []);

    if (statusPage) {
        return <ErrorPage status={statusPage} />;
    }

    if (!isAlbum && IS_HIDDEN_PUBLIC) {
        return (
            <div
                className={classNames(styles.root, styles.rootPassword, {
                    [styles.passwordThemed]: supportThemedPage,
                    [styles.rootPasswordB2B]: IS_B2B_BIZ_USER,
                })}
            >
                <Password />
            </div>
        );
    }

    if (isShove) {
        return <ShovePublic storage={storage} />;
    }

    return (
        <div className={classNames(styles.root, { [styles.root_signlePublic]: !isNotDownloadableFolder })}>
            <div className={styles.content}>
                {isNotDownloadableFolder ? (
                    <div
                        className={classNames(styles.folderContent, {
                            [styles.folderContent_shove]: isShove,
                            [styles.folderContent_right]: !noAds,
                            [styles.folderContent_newHeader]: NEW_PORTAL_HEADER,
                            [styles.folderContent_supportThemedPage]: supportThemedPage,
                            [styles.folderContent_overquota]: isOverquota && overquotaUserLockInPublic?.enable,
                        })}
                    >
                        {isOverquota && overquotaUserLockInPublic?.enable ? (
                            <Overquota overquotaSize={quota?.value} isFolder />
                        ) : supportThemedPage ? (
                            <BreadcrumbsWrapper>
                                <Folder noTopBanner={noTopBanner} supportThemedPage showUploadBlock={!showThemeChooser} />
                            </BreadcrumbsWrapper>
                        ) : (
                            <Folder noTopBanner={noTopBanner} />
                        )}
                    </div>
                ) : (
                    <div
                        className={classNames(styles.fileContent, {
                            [styles.fileContent_shove]: isShove,
                        })}
                    >
                        {isOverquota && overquotaUserLockInPublic?.enable ? (
                            <Overquota overquotaSize={quota?.value} />
                        ) : isShove || showDisabledForOverquotaOwner ? (
                            <File isShove={isShove} />
                        ) : (
                            <ReactViewerConnected
                                embedded={!isBizDownlodableFolder}
                                isViewer
                                disableClose={!IS_MY_TEAM}
                                gaCategory="indpnewv"
                            />
                        )}
                    </div>
                )}
            </div>
            {showThemeChooser && isOwn && <ThemeChooser />}
        </div>
    );
});
